/**
 * Document visibility subscription with an optional reactive wall clock.
 *
 * Covers UI that needs `isVisible` and/or a `nowMs` that refreshes on app/tab
 * resume and optionally on a timer (saved itinerary buckets, SearchBar refresh
 * button, FAB future-count badge).
 *
 * Imperative pause/resume paths (Map render throttler, GPS blink, persistence
 * flush) should subscribe to {@link subscribeToPageVisibilityTransitions}
 * directly instead of forcing a React re-render through this hook.
 *
 * Visibility comes from {@link PageLifecycleStore}, not from a raw
 * `visibilitychange` listener: Chrome may skip that event when thawing a frozen
 * page, which would leave consumers stuck on a stale `isVisible`.
 */

import { useEffect, useRef, useState } from "react";
import {
    pageLifecycleStore,
    subscribeToPageVisibilityTransitions,
} from "../services/lifecycle/PageLifecycleStore";

/** Options for {@link usePageVisibility}. */
export interface UsePageVisibilityOptions {

    /**
     * When `false`, skips visibility listeners and interval ticks so inactive
     * consumers do not re-render on tab switches.
     *
     * @defaultValue `true`
     */
    "enabled"?: boolean;

    /**
     * When `true`, maintains a reactive `nowMs` wall-clock sample.
     *
     * @defaultValue `false`
     */
    "trackNow"?: boolean;

    /**
     * When set and positive (and `trackNow` is `true`), refreshes `nowMs` on
     * this interval while `enabled` and the document is visible.
     */
    "tickIntervalMs"?: number;

    /**
     * When `true`, delays the first interval tick until the next
     * `tickIntervalMs` boundary (e.g. start of the next minute).
     *
     * @defaultValue `false`
     */
    "alignToIntervalBoundary"?: boolean;

    /**
     * Fired only from interval ticks (not on visibility resume or enable).
     * Useful for side effects such as SearchBar auto-updating the date field.
     */
    "onIntervalTick"?: () => void;
}

/** Visibility-only result. */
export interface PageVisibilityState {

    /** Whether `document.visibilityState` is currently `"visible"`. */
    "isVisible": boolean;
}

/** Visibility plus reactive wall-clock result. */
export interface PageVisibilityWithNowState extends PageVisibilityState {

    /** Wall-clock milliseconds, refreshed on resume / enable / optional tick. */
    "nowMs": number;
}

/**
 * Tracks document visibility, optionally with a reactive wall clock.
 *
 * @param options Visibility / clock options. When `trackNow` is `true`, the
 * result includes `nowMs`.
 * @returns Visibility state, and `nowMs` when `trackNow` is enabled.
 */
export function usePageVisibility(
    options: UsePageVisibilityOptions & { "trackNow": true },
): PageVisibilityWithNowState;

export function usePageVisibility(
    options?: UsePageVisibilityOptions,
): PageVisibilityState;

export function usePageVisibility( options: UsePageVisibilityOptions = {} ): PageVisibilityState | PageVisibilityWithNowState {
    const {
        "enabled": enabledOption = true,
        "trackNow": trackNowOption = false,
        tickIntervalMs,
        "alignToIntervalBoundary": alignToIntervalBoundaryOption = false,
        onIntervalTick,
    } = options;

    const [ isVisible, setIsVisible ] = useState<boolean>( () => document.visibilityState === "visible" );
    const [ nowMs, setNowMs ] = useState<number>( () => Date.now() );

    const onIntervalTickRef = useRef( onIntervalTick );
    useEffect( () => {
        onIntervalTickRef.current = onIntervalTick;
    }, [ onIntervalTick ] );

    useEffect( () => {
        if ( !enabledOption ) {
            return;
        }

        /**
         * Samples the wall clock when `trackNow` is enabled.
         */
        const refreshNowMs = (): void => {
            if ( trackNowOption ) {
                setNowMs( Date.now() );
            }
        };

        /**
         * Interval tick: refresh clock and invoke the optional interval-only callback.
         */
        const handleIntervalTick = (): void => {
            if ( !pageLifecycleStore.getSnapshot().isVisible ) {
                return;
            }

            refreshNowMs();
            onIntervalTickRef.current?.();
        };

        /*
         * Subscribing first starts the lifecycle store, so the deferred sync
         * below reads a freshly resynchronized snapshot.
         */
        const unsubscribeVisibility = subscribeToPageVisibilityTransitions( ( nextIsVisible ) => {
            setIsVisible( nextIsVisible );

            if ( nextIsVisible ) {
                refreshNowMs();
            }
        } );

        // Sync visibility (and optional clock) when becoming enabled.
        const enableRefreshTimeoutId = window.setTimeout( () => {
            setIsVisible( pageLifecycleStore.getSnapshot().isVisible );
            refreshNowMs();
        }, 0 );

        let boundaryTimeoutId: number | undefined;
        let intervalId: number | undefined;

        if (
            trackNowOption &&
            tickIntervalMs != null &&
            tickIntervalMs > 0
        ) {
            if ( alignToIntervalBoundaryOption ) {
                const millisecondsUntilBoundary = tickIntervalMs - ( Date.now() % tickIntervalMs );
                boundaryTimeoutId = window.setTimeout( () => {
                    handleIntervalTick();
                    intervalId = window.setInterval( handleIntervalTick, tickIntervalMs );
                }, millisecondsUntilBoundary );
            } else {
                intervalId = window.setInterval( handleIntervalTick, tickIntervalMs );
            }
        }

        return () => {
            window.clearTimeout( enableRefreshTimeoutId );
            unsubscribeVisibility();
            if ( boundaryTimeoutId !== undefined ) {
                window.clearTimeout( boundaryTimeoutId );
            }
            if ( intervalId !== undefined ) {
                window.clearInterval( intervalId );
            }
        };
    }, [
        alignToIntervalBoundaryOption,
        enabledOption,
        tickIntervalMs,
        trackNowOption,
    ] );

    if ( trackNowOption ) {
        return {
            isVisible,
            nowMs,
        };
    }

    return { isVisible };
}
